// Settings → Home screen: the parts of the phone's arrangement that are
// settings rather than gestures (the dock, the wallpaper, how icons look,
// what the outside widgets need, hidden apps), and a way back to the default. Everything else is arranged
// by hand on Home itself.
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { cx } from '@/components/ui'
import {
  defaultLayout,
  ICON_MAX,
  ICON_MIN,
  ICON_SIZES,
  launcherColumns,
  showApp,
  snapIconSize,
} from './layout'
import { tick } from '@/lib/haptics'
import { savePhone, usePhoneApps, usePhoneLayout } from './phone'
import { useCurrentWorkspace } from '@/db/hooks'
import { appIdsFor } from '@/lib/useNav'
import { suiteOf } from '@/suites'
import { AppLabel, AppTile } from './AppIcon'
import { DockPicker } from './DockPicker'
import { Backdrop, BACKDROPS } from './Backdrop'
import { WidgetSettings } from './WidgetSettings'

const Label = ({ children }: { children: string }) => (
  <h2 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">{children}</h2>
)

export const HomeSettings = () => {
  const layout = usePhoneLayout()
  const { byId } = usePhoneApps()
  // each suite has its own Home; Everything has the shared one
  const suite = useCurrentWorkspace()
  const manifest = suite ? suiteOf(suite) : undefined
  const [slot, setSlot] = useState<number | null>(null)
  const chip = (on: boolean) =>
    cx(
      'rounded-full px-3.5 min-h-9 text-sm border',
      on ? 'bg-accent text-on-accent border-transparent' : 'bg-raised border-line',
    )
  const hidden = layout.hidden.map((id) => byId.get(id)).filter((a) => !!a)
  return (
    <div className="flex flex-col gap-6" data-testid="home-settings">
      <p className="text-sm text-muted">
        <Link to="/home" className="text-accent-text underline">
          Home
        </Link>{' '}
        is arranged by hand: hold the background (or tap Edit) and things wiggle. Drag to move, tap for size
        and page, the minus takes something off, the plus adds widgets and apps. The layout follows you to
        every phone.
      </p>
      <p className="text-sm text-muted -mt-3" data-testid="home-whose">
        {suite
          ? `This is ${suite.spec.name}'s Home: each suite has its own.`
          : 'This is the Home for Everything; each suite has its own.'}
      </p>
      <section>
        <Label>Dock</Label>
        <div className="card p-3 grid grid-cols-4 gap-2">
          {layout.dock.map((id, i) => {
            const app = byId.get(id)
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSlot(i)}
                className="flex flex-col items-center gap-1.5 min-w-0"
                data-testid={`home-dock-${i}`}
              >
                {app ? (
                  <AppTile app={app} tint={layout.icons} />
                ) : (
                  <span className="h-12 w-12 rounded-[1.1rem] border border-dashed border-line" />
                )}
                <AppLabel>{app?.label ?? 'Choose'}</AppLabel>
              </button>
            )
          })}
          <div className="flex flex-col items-center gap-1.5 min-w-0 opacity-60">
            <span className="h-12 w-12 rounded-[1.1rem] border border-line bg-raised flex items-center justify-center text-xs">
              Apps
            </span>
            <AppLabel>Always</AppLabel>
          </div>
        </div>
        <p className="text-xs text-faint mt-1.5 px-1">
          Tap a slot to choose. While arranging Home you can tap the tabs themselves.
        </p>
      </section>
      <IconSizeSection />
      <section>
        <Label>Background</Label>
        {/* each tile is the real backdrop, moving, at the phone's proportions */}
        <div
          className="grid grid-cols-3 sm:grid-cols-[repeat(auto-fill,minmax(7rem,9.5rem))] gap-2.5"
          role="radiogroup"
          aria-label="Background"
          data-testid="backdrop-picker"
        >
          {BACKDROPS.map((b) => {
            const on = layout.wallpaper === b.id
            return (
              <button
                key={b.id}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={`${b.name}: ${b.blurb}`}
                onClick={() => void savePhone({ ...layout, wallpaper: b.id })}
                className="flex flex-col items-center gap-1.5 min-w-0"
                data-testid={`backdrop-${b.id}`}
              >
                <span
                  className={cx(
                    'relative w-full aspect-[9/16] overflow-hidden rounded-[var(--radius-card)] border bg-ink',
                    on ? 'border-accent ring-2 ring-accent/40' : 'border-line',
                  )}
                >
                  <Backdrop kind={b.id} />
                  {b.id === 'none' && (
                    <span className="absolute inset-0 flex items-center justify-center text-xs text-faint">
                      None
                    </span>
                  )}
                </span>
                <span className={cx('text-xs', on ? 'text-fg font-semibold' : 'text-muted')}>{b.name}</span>
              </button>
            )
          })}
        </div>
        <p className="text-xs text-faint mt-2 px-1">
          Behind Home and the Apps screen. It follows light, dark and black.
        </p>
      </section>
      <section>
        <Label>Icons</Label>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => void savePhone({ ...layout, icons: 'colour' })}
            className={chip(layout.icons === 'colour')}
          >
            Tinted
          </button>
          <button
            type="button"
            onClick={() => void savePhone({ ...layout, icons: 'mono' })}
            className={chip(layout.icons === 'mono')}
          >
            Plain
          </button>
        </div>
      </section>
      <WidgetSettings />
      <section>
        <Label>Hidden apps</Label>
        {hidden.length ? (
          <ul className="group-list">
            {hidden.map((a) => (
              <li key={a.key} className="flex items-center gap-3 px-3.5 min-h-12">
                <span className="flex-1 truncate">{a.label}</span>
                <button
                  type="button"
                  onClick={() => void savePhone(showApp(layout, a.key))}
                  className="text-sm text-accent-text min-h-10 px-2"
                >
                  Show
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-faint px-1">None. Hold an app in the launcher to hide it.</p>
        )}
      </section>
      <section>
        <button
          type="button"
          onClick={() => {
            if (confirm('Put the home screens, dock and launcher back as they came? Undo brings yours back.'))
              void savePhone(
                suite && manifest
                  ? manifest.home(appIdsFor({ nav: suite.spec.nav, packs: suite.spec.packs }))
                  : defaultLayout(),
              )
          }}
          className="rounded-[10px] bg-raised border border-line px-3.5 min-h-10 text-sm"
          data-testid="home-reset"
        >
          {manifest ? `Reset to ${manifest.title}'s` : 'Reset to the default'}
        </button>
      </section>
      <DockPicker slot={slot} layout={layout} onClose={() => setSlot(null)} />
    </div>
  )
}

const SIZE_NAMES: Record<number, string> = { 40: 'Small', 48: 'Default', 56: 'Large', 64: 'Largest' }
const PREVIEW_APPS = ['home', 'today', 'inbox', 'calendar', 'agents']

/**
 * Icon size: a slider that moves freely but settles on the common sizes as it
 * passes them (with a tick where the phone can), a preview at true size above
 * it, and one write when the finger lets go rather than one per pixel.
 */
const IconSizeSection = () => {
  const layout = usePhoneLayout()
  const { byId } = usePhoneApps()
  const [dragging, setDragging] = useState<number | null>(null)
  const size = dragging ?? layout.iconSize
  const cols = launcherColumns(size)
  const apps = PREVIEW_APPS.map((id) => byId.get(id)).filter((a) => !!a)
  const commit = () => {
    if (dragging !== null && dragging !== layout.iconSize) void savePhone({ ...layout, iconSize: dragging })
    setDragging(null)
  }
  return (
    <section data-testid="icon-size">
      <div className="flex items-baseline justify-between px-1 pb-1.5">
        <h2 className="text-[13px] font-semibold text-muted">Icon size</h2>
        <span className="text-[13px] text-fg-2 tabular" data-testid="icon-size-value">
          {size} pt{SIZE_NAMES[size] ? `, ${SIZE_NAMES[size]}` : ''}
        </span>
      </div>
      {/* the preview is drawn at true size: what you see here is what Home shows */}
      <div className="card p-4 mb-3 overflow-hidden" aria-hidden>
        <div
          className="grid gap-x-3 gap-y-3 transition-[grid-template-columns]"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {apps.slice(0, cols).map((a) => (
            <div key={a.key} className="flex flex-col items-center gap-1.5 min-w-0">
              <AppTile app={a} tint={layout.icons} size={size} />
              <AppLabel>{a.label}</AppLabel>
            </div>
          ))}
        </div>
      </div>
      <input
        type="range"
        min={ICON_MIN}
        max={ICON_MAX}
        step={0.5}
        value={size}
        onChange={(e) => {
          const next = snapIconSize(Number(e.target.value))
          // a detent: the phone ticks as the slider settles on a common size
          if (next !== size && ICON_SIZES.includes(next)) tick(6)
          setDragging(next)
        }}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="range w-full"
        aria-label="Icon size"
        aria-valuetext={`${size} points`}
        data-testid="icon-size-slider"
      />
      {/* ticks under the slider at the common sizes, named where it helps */}
      <div className="relative h-6 mx-[9px] mt-0.5" aria-hidden>
        {ICON_SIZES.map((s) => (
          <span
            key={s}
            // the end labels lean inward so they never hang off the track
            className={cx(
              'absolute top-0 flex flex-col gap-0.5',
              s === ICON_MIN
                ? 'items-start'
                : s === ICON_MAX
                  ? '-translate-x-full items-end'
                  : '-translate-x-1/2 items-center',
            )}
            style={{ left: `${((s - ICON_MIN) / (ICON_MAX - ICON_MIN)) * 100}%` }}
          >
            <span className={cx('h-1.5 w-px', s === size ? 'bg-accent' : 'bg-line-strong')} />
            {SIZE_NAMES[s] && (
              <span className={cx('text-[11px] whitespace-nowrap', s === size ? 'text-fg' : 'text-faint')}>
                {SIZE_NAMES[s]}
              </span>
            )}
          </span>
        ))}
      </div>
      <p className="text-xs text-faint mt-1.5 px-1">Apps shows four across from 60 pt, so names still fit.</p>
    </section>
  )
}
